import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useVoucherCode } from "@/hooks/voucherCode/useVoucherCode";
import { useSnackbar } from "notistack";

export const VoucherCodeInput = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [inputCode, setInputCode] = useState("");

  const { redeemCode, isRedeeming, cooldownRemaining, isOnCooldown } = useVoucherCode({
    onSuccess: () => {
      enqueueSnackbar("Voucher code redeemed successfully!", {
        variant: "success",
        autoHideDuration: 5000,
      });
      setInputCode("");
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Invalid voucher code. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    },
  });

  const handleSubmit = () => {
    const trimmedCode = inputCode.trim();

    if (!trimmedCode) {
      enqueueSnackbar("Please enter a voucher code", {
        variant: "error",
        autoHideDuration: 3000,
      });
      return;
    }

    if (isOnCooldown) {
      enqueueSnackbar(`Please wait ${cooldownRemaining} seconds before trying again`, {
        variant: "error",
        autoHideDuration: 3000,
      });
      return;
    }

    redeemCode({ code: trimmedCode });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !isRedeeming && !isOnCooldown) {
      handleSubmit();
    }
  };

  const isDisabled = isRedeeming || isOnCooldown || !inputCode.trim();
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row gap-2">
        {/* Input container */}
        <div
          className="flex-1 relative md:bg-cede-bg-component md:border border-cede-border-container rounded-lg md:rounded-xl lg:rounded-2xl md:p-2 lg:p-3"
          style={{
            backdropFilter: "blur(20px)",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Visual input field background with bluish tint - desktop only */}
          <div
            className={`hidden md:block absolute md:left-[8px] md:right-[114px] md:top-[8px] md:bottom-[8px] lg:left-[12px] lg:right-[118px] lg:top-[12px] lg:bottom-[12px] rounded-md pointer-events-none transition-all duration-200 ${
              isFocused
                ? "bg-cede-primary/20"
                : isHovered
                  ? "bg-cede-primary/12"
                  : inputCode.trim()
                    ? "bg-cede-primary/15"
                    : "bg-cede-primary/8"
            }`}
            style={{
              boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          />

          <input
            type="text"
            placeholder="Enter code..."
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            onKeyDown={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={isRedeeming || isOnCooldown}
            className="w-full h-[56px] px-4 md:pr-[114px] py-4 bg-cede-bg-component border border-cede-border-container md:bg-transparent md:border-none rounded-md md:rounded-lg lg:rounded-xl text-cede-text-primary font-semibold text-[16px] leading-[140%] tracking-[0.02em] placeholder:text-cede-text-secondary placeholder:font-normal focus:outline-none focus:border-cede-primary transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed relative"
            style={{
              colorScheme: "dark",
            }}
            autoComplete="off"
          />
          {/* Button - absolutely positioned on desktop only */}
          <Button
            onClick={handleSubmit}
            disabled={isDisabled}
            variant="primary"
            size="small"
            className="hidden md:block absolute md:right-[10px] lg:right-[14px] top-1/2 -translate-y-1/2 h-[36px] px-4 text-sm disabled:opacity-70 disabled:bg-cede-primary/50 z-10"
          >
            {isRedeeming ? "..." : "Submit"}
          </Button>
        </div>

        {/* Button - separate on mobile */}
        <Button
          onClick={handleSubmit}
          disabled={isDisabled}
          variant="primary"
          size="small"
          className="md:hidden w-full text-sm disabled:opacity-70 disabled:bg-cede-primary/50"
        >
          {isRedeeming ? "..." : "Submit"}
        </Button>
      </div>
      {isOnCooldown && (
        <p className="text-xs text-cede-text-secondary mt-2 pl-2 md:pl-2 lg:pl-3">
          Please wait {cooldownRemaining} seconds before trying again
        </p>
      )}
    </div>
  );
};
